import { MirrorSelect } from '@crasman/multiselect';

/**
 * Initialize multiselect fields
 *
 * @class multiselect
 */
class multiselect {

  constructor() {
    this.dropdownElements = document.querySelectorAll('.js-select, .js-multiselect');
  }

  /**
   * Initializes the multiselect module.
   */
  init(){
    this.initializeDropdowns(this.dropdownElements);
    const multiContainers = document.querySelectorAll('.multiselect-container');
    multiContainers.forEach(container => this.initializeMultiselect(container));
  }

  /**
   * Initializes dropdowns with MirrorSelect.
   *
   * @param {NodeList} dropdownElements - List of dropdown elements to initialize.
   */
  initializeDropdowns(dropdownElements) {
    const searchableOptions = {
      searchable: true,
      selectionClears: true,
      enterSelects: true,
    };

    const settings = {
      listElement: { size: 12 },
      buttonElement: { showPlaceholder: true },
      mirrorNative: true,
      setCssProperties: false,
    };

    dropdownElements.forEach(select => {
      if (select.dataset?.searchable) {
        settings.searchable = searchableOptions;
      }
      new MirrorSelect(select, settings);
    });
  }

  /**
   * Initializes a single multiselect container.
   *
   * @param {HTMLElement} container - The multiselect container element.
   */
  initializeMultiselect(container) {
    const multisearch = container.querySelector('.multiselect-search');
    if (!multisearch) return;

    const searchInput = multisearch.querySelector('input');
    const selectElement = container.querySelector('select');
    const multiselectList = container.querySelector('.multiselect-list');
    const multiselectButton = container.querySelector('.multiselect-button');
    const options = container.querySelectorAll('.multiselect-option');

    this.handleSearchInput(searchInput, selectElement, container, multiselectList);
    this.addCheckboxesToOptions(selectElement, options);
    this.addUnselectAllButton(selectElement, multiselectList, options, searchInput, multiselectButton);
    this.updatePlaceholderOnSelection(selectElement, searchInput);
  }

  /**
   * Handles search input events for filtering options.
   *
   * @param {HTMLInputElement} searchInput - The search input element.
   * @param {HTMLSelectElement} selectElement - The select element.
   * @param {HTMLElement} container - The multiselect container element.
   * @param {HTMLElement} multiselectList - The multiselect list element.
   */
  handleSearchInput(searchInput, selectElement, container, multiselectList) {
    searchInput.addEventListener('focus', () => {
      const openMultiselect = document.querySelector('.multiselect-container--open');
      if (openMultiselect && openMultiselect !== container) {
        openMultiselect.classList.remove('multiselect-container--open');
        openMultiselect.querySelector('.multiselect-button').setAttribute('aria-expanded', 'false');
        openMultiselect.querySelector('.multiselect-list').setAttribute('hidden', '');
      }
    });

    // Make filter has options in two different groups, "Suosituimmat" and "Aakkosittain".
    // Text search of mirror select is not able to filter options in correct way in both groups.
    // That's why we need to create a workaround and filter options manually in "Aakkosittain".
    searchInput.addEventListener('input', () => {
      if (selectElement.name === 'make') {
        const multiselectGroup = container.querySelectorAll('li.multiselect-group');
        const popularOptions = multiselectGroup[0]?.querySelectorAll('.multiselect-option');
        const allOptions = multiselectGroup[1]?.querySelectorAll('.multiselect-option');

        if (popularOptions && allOptions) {
          popularOptions.forEach(option => {
            const allOption = Array.from(allOptions).find(allOption => allOption.dataset.value === option.dataset.value);
            if (option.hidden) {
              allOption.setAttribute('hidden', '');
              allOption.setAttribute('aria-hidden', true);
            } else {
              allOption.removeAttribute('hidden');
              allOption.removeAttribute('aria-hidden');
            }
          });
        }
      }
    });
  }

  /**
   * Adds checkboxes to multiselect options.
   *
   * @param {HTMLSelectElement} selectElement - The select element.
   * @param {NodeList} options - List of multiselect options.
   */
  addCheckboxesToOptions(selectElement, options) {
    if (!selectElement.hasAttribute('multiple')) return;

    options.forEach(option => {
      const checkboxContainer = document.createElement('span');
      checkboxContainer.classList.add('multiselect-option__checkbox-container');
      option.appendChild(checkboxContainer);

      const checkbox = document.createElement('span');
      checkbox.classList.add('multiselect-option__checkbox');
      checkboxContainer.appendChild(checkbox);
    });
  }

  /**
   * Adds an "Unselect All" button to the multiselect list.
   *
   * @param {HTMLSelectElement} selectElement - The select element.
   * @param {HTMLElement} multiselectList - The multiselect list element.
   * @param {NodeList} options - List of multiselect options.
   * @param {HTMLInputElement} searchInput - The search input element.
   * @param {HTMLElement} multiselectButton - The multiselect button element.
   */
  addUnselectAllButton(selectElement, multiselectList, options, searchInput, multiselectButton) {
    if (!selectElement.hasAttribute('multiple')) return;

    const unselectAllButtonContainer = document.createElement('li');
    unselectAllButtonContainer.classList.add('multiselect-list__unselect-all');
    unselectAllButtonContainer.innerHTML = '<button class="btn tertiary">Tyhjennä valinnat</button>';
    multiselectList.appendChild(unselectAllButtonContainer);

    const unselectAllButton = unselectAllButtonContainer.querySelector('button');
    // Because keyboard using does not work properly with this button, disable the focus with keyboard
    unselectAllButton.setAttribute('tabindex', '-1');

    unselectAllButton.addEventListener('click', () => {
      options.forEach(opt => {
        opt.classList.remove('selected');
        opt.removeAttribute('aria-selected');
      });
      selectElement.querySelectorAll('option').forEach(opt => opt.removeAttribute('selected'));
      searchInput.placeholder = '';
      selectElement.value = '';
      searchInput.value = '';
      // For some reason, if options are selected with keyboard and then unselected with this button,
      // the multiselect button does not update its placeholder text. This fixes that.
      multiselectButton.classList.remove('placeholder');
      multiselectButton.querySelector('span').textContent = multiselectButton.dataset.placeholder;
    });
  }

  /**
   * Updates the placeholder text when selection changes.
   *
   * @param {HTMLSelectElement} selectElement - The select element.
   * @param {HTMLInputElement} searchInput - The search input element.
   */
  updatePlaceholderOnSelection(selectElement, searchInput) {
    selectElement.addEventListener('change', (e) => {
      const options = Array.from(e.target.selectedOptions)
        .filter(option => option.value)
        .map(option => option.label);
      searchInput.placeholder = options.join(', ');

    });
  }
}

export default new multiselect();
